import React, { useState } from "react";
import { SHOW_ORDER_PRICES } from "./helpers/orderPricing";
import { RootState } from "shared/redux/store/store";

import GenericCard from "@ui-components/Card/GenericCard";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericModal from "@ui-components/Modal/GenericModal";
import { saveOrderAsync, updateOrder } from "@redux/slices/kontrolEdge/orderSlice";
import { useAppDispatch, useAppSelector } from "shared/redux/hooks";
import { GenericRadioCard } from "@ui-components/RadioCard/GenericRadioCard";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { calculatePricingOptions } from "./helpers/calcutalePricingOptions";
import GenericInput from "@ui-components/Input/GenericInput";
import { useNavigate } from "react-router-dom";


interface SummaryProps {
  currentStep: number;
  token: string;
}

const Summary: React.FC<SummaryProps> = ({ currentStep, token }) => {
  // Modal & term state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newTerm, setNewTerm] = useState<12 | 24 | 36>(12);
  const [newPrice, setNewPrice] = useState<number>(300);
  const [orderName, setOrderName] = useState('');

  // Redux hooks
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const portData = useAppSelector((state: RootState) => state.order.ports[0]);
  const pricingPlans = useAppSelector((state: RootState) => state.order.pricingPlans);
  const storedOrderName = useAppSelector((state: RootState) => state.order.name);
  
  // Extract values from portData
  const selectedCapacityLabel = portData?.configuration?.capacity?.speed || "Not specified";
  const selectedCapacityTypeLabel = portData?.configuration?.capacity?.type || "Not specified";
  const selectedContractMonths = portData?.configuration?.term?.months || "";
  const selectedContractPrice = portData?.pricing?.totalMRC || "";
  const selectedContractPriceCurrency = portData?.pricing?.currency || "";
  const selectedLocation = portData?.location?.facilityName || "Not specified";

  // console.log(portData, 'port data summary')

  // For cross connect, we display it only if crossConnect is "requireXC" or "lastMile"
  const showCrossConnect =
    portData?.configuration?.crossConnect === "requireXC" ||
    portData?.configuration?.crossConnect === "lastMile";
  // If crossConnectPricing is stored as a string, try converting it to number
  const selectedCrossPricing = Number(portData?.configuration?.crossConnectPricing) || 0;
  // Calculate Subtotal (for Step 3)
  const contractPrice = Number(selectedContractPrice) || 0;
  const subtotal = contractPrice + (showCrossConnect ? selectedCrossPricing : 0);
  // Calculate pricing options using your shared helper
  const pricingOptions = calculatePricingOptions(
    pricingPlans ?? null,
    portData?.configuration?.capacity?.type ?? "single",
    portData?.configuration?.capacity?.speed ?? "10gbps"
  );
  const selectedOption = portData?.configuration?.term?.months
  ? portData.configuration.term.months.toString()
  : "12";
  // Find the selected pricing option using the selectedOption (which is a string of months)
  const pricingOption = pricingOptions.find((opt) => opt.value === selectedOption);

  // Determine the header title based on whether NRC pricing applies
  const headerTitle = !SHOW_ORDER_PRICES
    ? "Details"
    : pricingOption?.nrcApplied
    ? "Non Recurring Cost (NRC)"
    : "Monthly Recurring Cost (MRC)";

  // Alert state variables for dynamic alert configuration
  const [isResultAlertOpen, setIsResultAlertOpen] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [alertDescription, setAlertDescription] = useState("");
  const [alertVariant, setAlertVariant] = useState<"success" | "error" | "confirm" | undefined>(undefined);

  // Function to update the term (Change Term modal)
  const handleTermChange = (newTerm: 12 | 24 | 36, newPrice: number) => {
    const updatedPortData: Partial<typeof portData> = {
      configuration: {
        term: { months: newTerm, autoRenew: false },
      },
      pricing: {
        totalMRC: newPrice,
        currency: "USD",
      },
    };
    dispatch(updateOrder({ portIndex: 0, portData: updatedPortData }));
    setIsModalOpen(false);
  };

  // Function for saving order as draft
  const handleSaveForLater = async () => {
    try {
      await dispatch(saveOrderAsync({ token, orderName })).unwrap();
      setAlertTitle("Your Order has been saved for later");
      setAlertDescription("You can access to your orders trough the cart icon next to your profile.");
      setAlertVariant("success");
      setIsResultAlertOpen(true);
    } catch (error) {
      console.error(error);
      setAlertTitle("Error");
      setAlertDescription("There was an error saving your order. Please try again.");
      setAlertVariant("error");
      setIsResultAlertOpen(true);
    }
  };

    // Header content – if step 4, include the Change Term button in the header.
    const headerContent =
    currentStep === 4 ? (
      <div className="flex w-full items-center justify-between">
        <h3 className="font-bold">{headerTitle}</h3>
        <div className="flex gap-2">
          <GenericButton
            label={
              <div className="flex items-center">
                <span className="font-medium text-semantic-text-brand">
                  Change term
                </span>
              </div>
            }
            variant="refresh"
            size="md"
            onClick={() => setIsModalOpen(true)}
          />
      </div>
    </div>
    ) : (
      <div className="flex w-full items-center justify-between">
        <h3 className="font-bold">{headerTitle}</h3>
      </div>
    );

  // Conditionally render the order name field: show a text if order is loaded, or an input if not.
  const isOrderLoaded = storedOrderName && storedOrderName.trim() !== "";
  const orderNameField = isOrderLoaded ? (
    <p className="text-sm font-medium"> Order Name: {storedOrderName}</p>
  ) : (
    <GenericInput
      label="Enter a name to identify your order"
      placeholder="E.g. Order123"
      value={orderName}
      onChange={setOrderName}
      className="text-small w-small"
    />
  );


  // Body content – conditional based on currentStep.
  let bodyContent;
  if (currentStep === 4) {
    // For step 4, show only the Subtotal row.
    bodyContent = (
      <>
        <div className="flex justify-between w-full py-1">
          <h3 className="text-gray-400">Subtotal</h3>
          <p className="font-medium text-semantic-text-brand">{SHOW_ORDER_PRICES ? `$${subtotal} USD / month` : "—"}</p>
        </div>
        <div className="flex justify-center w-full py-1">
          {orderNameField}
        </div>
        <div className="flex justify-end">
          <GenericButton
            label={
              <div className="flex items-center">
                <span className="font-medium text-semantic-text-primary">
                  Save for later
                </span>
              </div>
            }
            variant="secondary"
            size="md"
            onClick={handleSaveForLater}
            disabled={orderName ? false : true}
          />
        </div>
      </>
    );
  } else {
    // For steps 1-3, show full details.
    bodyContent = (
      <>
        {/* Port Capacity & Port Type */}
        <div className="flex justify-between w-full py-1">
          <h3 className="text-gray-400">Port Capacity - Port Type</h3>
          <p className="text-gray-400">
            {selectedCapacityLabel}, {selectedCapacityTypeLabel}
          </p>
        </div>
        {/* Location */}
        <div className="flex justify-between w-full py-1">
          <h3 className="text-gray-400">Location</h3>
          <p className="text-gray-400">{selectedLocation}</p>
        </div>
        {/* Contract Term */}
        {selectedContractMonths && (
          <div className="flex justify-between w-full py-1">
            <h3 className="font-medium">{`${selectedContractMonths} Months Term`}</h3>
            {SHOW_ORDER_PRICES && (
              <p className="font-medium">{`$${selectedContractPrice} ${selectedContractPriceCurrency} / month`}</p>
            )}
          </div>
        )}
        {/* Cross Connect Price (if applicable) */}
        {SHOW_ORDER_PRICES && (currentStep === 2 || currentStep === 3) && showCrossConnect && (
          <div className="flex justify-between w-full py-1 border-b mb-5">
            <h3 className="font-medium">Cross Connect Price</h3>
            <p className="font-medium">{`$${selectedCrossPricing} USD / month`}</p>
          </div>
        )}
        {/* For Step 3: Subtotal */}
        {SHOW_ORDER_PRICES && currentStep === 3 && (
          <div className="flex justify-between w-full py-1">
            <h3 className="text-gray-400">Subtotal</h3>
            <p className="font-medium text-semantic-text-brand">{`$${subtotal} USD / month`}</p>
          </div>
        )}
        {/* Change Term Button for steps 1-3 */}
        {(currentStep === 1 ||
          currentStep === 2 ||
          currentStep === 3) && (
          <div className="flex justify-end w-full py-1">
            <GenericButton
              label={
                <div className="flex items-center">
                  <span className="font-medium text-semantic-text-brand">
                    Change Term
                  </span>
                </div>
              }
              variant="refresh"
              size="md"
              className="px-0"
              onClick={() => setIsModalOpen(true)}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <div className="px-7 pt-5">
      <h3 className="text-2l font-bold mb-4 text-left text-gray-400">
        {currentStep === 3 ? "Overview" : "Summary"}
      </h3>
        <GenericAlert
            isOpen={isResultAlertOpen}
            onClose={() => setIsResultAlertOpen(false)}
            onConfirm={() => {
              setIsResultAlertOpen(false);
              navigate("/kontrol");
            }}
            title={alertTitle}
            description={alertDescription}
            cancelLabel="Close"
            confirmLabel="Ok"
            variant={alertVariant}
        />
        <GenericCard
          isCollapsible={false}
          headerContent={headerContent}
          bodyContent={bodyContent}
          variant={currentStep === 4 ? "secondary" : "primary"}
          shadow="flat"
          hasBorder={true}  
        />
      {/* Modal for Changing Term */}
      <GenericModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Change Term"
        size="sm"
      >
        <div className="flex flex-wrap justify-evenly py-5 gap-y-5 border-t border-b border-gray-300">
        {pricingOptions.map((option) => (
          <GenericRadioCard
            key={option.value}
            name="contract-term"
            value={option.value}
            label={option.label}
            checked={newTerm === option.term}
            onChange={() => {
              setNewTerm(option.term as 12 | 24 | 36);
              setNewPrice(option.price);
            }}
          />
        ))}
        </div>
        <div className="flex justify-end mt-6 space-x-3">
          <GenericButton
            label="Aceptar"
            variant="brand"
            onClick={() => handleTermChange(newTerm, newPrice)}
          />
        </div>
      </GenericModal>
      <div className="mt-5 w-80% border-b border-gray-300"></div>
    </div>
  );
};

export default Summary;